import { Divider, Group } from '@mantine/core';
import { useMemo, useRef, useState } from 'react';
import type {
  GetAvailableTargetsResponse,
  TransformedTarget,
} from '../../../api/targets-client/targets-client.type.ts';
import type { MeasureMetadata, WorkPeriod } from '../../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { canvasHtmlDownload } from '../../../helpers/image-downloader/image-downloader.ts';
import { useGlobalStore } from '../../../store/global-store/global-store.ts';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { Checkbox } from '../../../ui-library/checkbox/checkbox.tsx';
import { Select } from '../../../ui-library/select/select.tsx';
import { Text } from '../../../ui-library/typography/typography';
import { FloraButton } from '../../flora/flora-button.tsx';
import { WorkPeriodChartHeader } from '../components/work-period-chart-header.tsx';
import { MeasuresOverTimeChart } from './components/measures-over-time-chart';
import { formatWorkPeriodDataForFlora } from './helpers/measure-comparison-chart.helpers.ts';

interface ComparisonMeasuresOverTimeProps {
  customMeasures: MeasureMetadata[];
  measureData: Record<string, any>;
  workPeriods: WorkPeriod[];
  targets: GetAvailableTargetsResponse;
  handleMeasureClick: (measure: string, workPeriodName: string, title: string, date?: string | null) => void;
}

export const ComparisonMeasuresOverTime = ({
  customMeasures,
  measureData,
  workPeriods,
  targets,
  handleMeasureClick,
}: ComparisonMeasuresOverTimeProps) => {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);
  const downloadRef = useRef<HTMLDivElement>(null!);
  const [showAverageLine, setShowAverageLine] = useState(false);
  const { selectedMeasureForOverTime, setSelectedMeasureForOverTime } = useWorkPeriodStore();

  const sortedMeasures = useMemo(() => {
    return [...customMeasures].sort((a, b) => a.measure_title.localeCompare(b.measure_title));
  }, [customMeasures]);

  function getUniqueTargets(items: TransformedTarget[]): TransformedTarget[] {
    const seenIds = new Set<number | string>();
    return items.filter((item) => {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });
  }
  const flatTargets = getUniqueTargets(Object.values(targets).flat());

  const handleDownload = () => {
    if (downloadRef.current) {
      try {
        canvasHtmlDownload(`${i18n.t('common.sprint')}_comparison_measures_over_time`, downloadRef);
      } catch (error) {
        console.error('Error downloading chart', error);
      }
    }
  };

  const floraData = formatWorkPeriodDataForFlora(
    measureData,
    customMeasures,
    workPeriods,
    [selectedMeasureForOverTime?.measure_name || ''],
    true,
  );
  return (
    <div ref={downloadRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <WorkPeriodChartHeader
        title={`${i18n.t('common.sprint')} Comparison`}
        onExport={handleDownload}
        hideRadio
        floraButton={
          useAI ? (
            <FloraButton
              externalData={[floraData]}
              prompt={i18n.t('flora.work_periods.sprint_measures_over_time_chart_prompt')}
              tooltipText={i18n.t('flora.work_periods.flora_button_tooltip')}
            />
          ) : undefined
        }
      />
      <Group mt="16px" mb="16px" justify="space-between">
        <Text weight="bold">
          {selectedMeasureForOverTime ? selectedMeasureForOverTime.measure_title : 'Select a measure'}
        </Text>
        <Group gap="xs">
          <Checkbox
            label="Show Average Line"
            checked={showAverageLine}
            onChange={(event) => setShowAverageLine(event.currentTarget.checked)}
          />
          <Divider orientation="vertical" />
          <Select
            value={selectedMeasureForOverTime?.measure_name || null}
            options={sortedMeasures.map((measure) => ({ value: measure.measure_name, label: measure.measure_title }))}
            onChange={(value) =>
              setSelectedMeasureForOverTime(customMeasures.find((measure) => measure.measure_name === value) || null)
            }
            placeholder="Select a measure"
            searchable
          />
        </Group>
      </Group>
      <div style={{ flex: 1, minHeight: 0 }}>
        <MeasuresOverTimeChart
          selectedMeasure={selectedMeasureForOverTime}
          measureData={measureData}
          workPeriods={workPeriods}
          targets={flatTargets}
          showAverageLine={showAverageLine}
          handleMeasureClick={handleMeasureClick}
        />
      </div>
    </div>
  );
};
